import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Image,
  Spacer,
  Spinner,
  Tag,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import BottomDrawer from "../../components/BottomDrawer";
import {
  getMockMusicFestVenues,
  useMusicFestivalGigs,
  useMusicFestivalVenues,
} from "../../data/musicFestival.api";
import {
  Gig,
  MusicFestivalFilter,
  Venue,
  VenueAndGig,
} from "../../types/MusicFestivalTypes";
import { MapPin } from "../../components/Map";
import { MapContext } from "../../context/map.context";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { IoChevronBack } from "react-icons/io5";
import { FaFilter } from "react-icons/fa";
import { filterMusicFestVenuesAndGigs } from "./util/Filtering";
import HybridBottomSheetDrawer from "../../components/HybridBottomSheetDrawer";
import { convertDateToDateString } from "../../util/dateFormatters";
import FilterTagList from "../../components/musicfestival/FilterTagList";
import LocationButton from "../../components/LocationButton";

const SearchedVenue = () => {
  const { locationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLocation = useLocation();
  const navigate = useNavigate();

  const { setPins, flyTo, setVenues } = useContext(MapContext);
  const [eventPins, setEventPins] = useState<MapPin[]>([]);
  const [locations, setLocations] = useState<Venue[]>([]);
  const [gigs, setGigs] = useState<Gig[]>([]);
  const [filterList, setFilterList] = useState<string[]>([]);
  const [venuesAndGigs, setVenuesAndGigs] = useState<VenueAndGig[]>([]);
  const [currentVenueAndGigs, setCurrentVenueAndGigs] = useState<VenueAndGig>();
  const [filter, setFilter] = useState<MusicFestivalFilter>({
    search: "",
    location: [],
    genre: [],
    accessibility: [],
  });

  const { data: gigData, loading: gigLoading } = useMusicFestivalGigs();

  useEffect(() => {
    //@ts-ignore
    if (searchParams.size > 0) {
      const searchTerm = searchParams.get("search");
      setFilter({
        search: searchTerm !== null ? searchTerm : "",
        location: searchParams.getAll("venue"),
        genre: searchParams.getAll("genre"),
        accessibility: searchParams.getAll("accessibility"),
      });
      if (searchTerm !== null) {
        setFilterList([
          ...searchParams.getAll("genre"),
          ...searchParams.getAll("venue"),
          ...searchParams.getAll("accessibility"),
          searchTerm !== null ? searchTerm : "",
        ]);
      } else {
        setFilterList([
          ...searchParams.getAll("genre"),
          ...searchParams.getAll("venue"),
          ...searchParams.getAll("accessibility"),
        ]);
      }
    }
  }, [urlLocation.search]);

  // stores gigs
  useEffect(() => {
    if (gigData && gigData.getPublishedEvents) {
      const fetchedGigs: Gig[] = gigData.getPublishedEvents;
      setGigs(fetchedGigs);
    }
  }, [gigData]);

  // applies url filters and finds current venue and gigs
  useEffect(() => {
    if (gigs.length > 0) {
      let res = filterMusicFestVenuesAndGigs(gigs, filter);
      setVenuesAndGigs(res);
      for (const item of res) {
        if (item.venue.id === locationId) {
          setCurrentVenueAndGigs(item);
          break;
        }
      }
    }
  }, [gigs, filter, locationId]);

  // Moves map to lancaster town centre instead of university campus
  useEffect(() => {
    flyTo({
      pitch: 1,
      bearing: 1,
      zoom: 16,
      center: {
        lng: -2.803113,
        lat: 54.048958,
      },
    });
  }, [flyTo]);

  // renders filtered down map pins
  useEffect(() => {
    const pins = venuesAndGigs.map((venueAndGigs) => {
      return {
        lng: venueAndGigs.venue.longitude,
        lat: venueAndGigs.venue.latitude,
        color: `${venueAndGigs.venue.id === locationId ? "blue" : "#E9464F"}`,
        size: 25,
        onClick: () =>
          navigate(
            `/musicfestival/search/${
              venueAndGigs.venue.id
            }?${searchParams.toString()}`
          ),
      };
    });
    setEventPins(pins);
  }, [venuesAndGigs]);

  useEffect(() => {
    setPins([...eventPins]);
  }, [eventPins, setPins]);

  console.log(filterList);

  return (
    <>
      <FilterTagList filterList={filterList} />
      <HybridBottomSheetDrawer ml={"4em"} maxDrawerHeight={window.innerHeight * 0.75}>
        <IconButton
          aria-label={"Return to music festival home page button"}
          icon={<IoChevronBack />}
          zIndex={999}
          onClick={() => {
            navigate(`/musicfestival/search?${searchParams.toString()}`);
          }}
          position="absolute"
          top={2}
          right={2}
        />

        <Box p={8}>
          <Flex mb={4} align="center">
            <Heading pr={"1rem"} size="md">
              Venue
            </Heading>
            <LocationButton variant="outline"></LocationButton>
          </Flex>
          <HStack align={"start"} w={"100%"} h={"5.5em"} spacing={"1em"}>
            {!currentVenueAndGigs && gigLoading && (
              <HStack w={"100%"} justify={"center"}>
                <Text>Loading Venue</Text>
                <Spinner />
              </HStack>
            )}
            {/* Error message when no venue is found (incorrect venue id or venue no longer exists) */}
            {!currentVenueAndGigs && !gigLoading && (
              <Alert
                flexDirection={"column"}
                gap={1}
                status="error"
                h={"100%"}
                borderRadius={"1em"}
              >
                <AlertTitle>Could Not Find This Venue</AlertTitle>
                <Button size={"sm"} onClick={() => navigate("/musicfestival")}>
                  Return to Venues Page
                </Button>
              </Alert>
            )}
            {/* Renders Current Venue */}
            {currentVenueAndGigs?.venue.imageUrl && (
              <Image
                style={{ aspectRatio: "1/1" }}
                maxH={"100%"}
                borderRadius={"xl"}
                src={currentVenueAndGigs?.venue.imageUrl}
                alt="Image of music festival venue"
              />
            )}
            <VStack align={"start"} justify={"start"} h={"100%"} spacing={0}>
              <Heading size={"sm"} mb={"0em"}>
                {currentVenueAndGigs?.venue.title}
              </Heading>
              <Spacer />
              <Text>{currentVenueAndGigs?.venue.description}</Text>
              <Spacer />
            </VStack>
          </HStack>
          <Flex mt={6} mb={6} justify="space-between" align="center">
            <Heading size="md">Gigs at Venue</Heading>
          </Flex>
          <VStack w={"100%"}>
            {currentVenueAndGigs &&
              currentVenueAndGigs?.gigs?.length < 1 &&
              gigLoading && (
                <HStack w={"100%"} justify={"center"}>
                  <Text>Loading Gigs</Text>
                  <Spinner />
                </HStack>
              )}
            {/* Message for no gigs at this venue */}
            {!gigLoading && currentVenueAndGigs?.gigs.length === 0 && (
              <Text>There are no upcoming gigs at this venue</Text>
            )}
            {/* Renders array of gigs at the selected venue */}
            {currentVenueAndGigs?.gigs.map((gig) => {
              const dateString = convertDateToDateString(gig.startDateUTC);
              return (
                <VStack
                  onClick={() => {
                    navigate(
                      `/musicfestival/${locationId}/${
                        gig.id
                      }?platform=mobile&${searchParams.toString()}`
                    );
                  }}
                  align={"start"}
                  justify={"start"}
                  h={"100%"}
                  w={"100%"}
                  spacing={0}
                >
                  <HStack w={"100%"}>
                    <Heading size={"sm"} color={"#717D96"}>
                      {gig.eventCategory}
                    </Heading>
                    <Spacer />
                    <Tag bg={"#D4D4D8"} color={"#89898F"}>
                      {gig.tags[0].name}
                    </Tag>
                  </HStack>
                  <Heading size={"sm"}>{gig.eventName}</Heading>
                  <HStack w={"100%"}>
                    <Text>{dateString}</Text>
                    <Spacer />
                    <Text>
                      {gig.startTime} - {gig.endTime}
                    </Text>
                  </HStack>
                  <Text>{gig.locations?.[0].title}</Text>
                </VStack>
              );
            })}
          </VStack>
        </Box>
      </HybridBottomSheetDrawer>
    </>
  );
};

export default SearchedVenue;
